import api from './client'

export type AppointmentColor = 'primary' | 'green' | 'yellow'

export interface AppointmentLead {
  id: string
  name: string
  phone: string | null
  email: string | null
  /** Lead source (e.g. facebook, instagram) — null when the lead has none. */
  source: string | null
}

export interface AppointmentUser {
  id: string
  firstName: string
  lastName: string
}

/** A proforma option. `label` is composed server-side as "number - full lead name". */
export interface ProformaOption {
  id: string
  number: string
  leadName: string
  label: string
  total: string
  currency: string
  status: string
}

export interface Appointment {
  id: string
  companyId: string
  leadId: string | null
  lead: AppointmentLead | null
  title: string
  description: string | null
  color: AppointmentColor
  isAllDay: boolean
  /** YYYY-MM-DD, set only when isAllDay */
  allDayDate: string | null
  startAt: string | null
  endAt: string | null
  proformaId: string | null
  proforma: ProformaOption | null
  createdBy: string
  createdByUser: AppointmentUser | null
  createdAt: string
  updatedAt: string
}

/**
 * `null` and `undefined` mean different things on a PATCH: `undefined` is dropped
 * from the JSON body and leaves the stored value alone (what a drag-to-move
 * wants), while an explicit `null` clears the field (what the edit form wants
 * when the user empties a picker). Fields that can be cleared are nullable here.
 */
export interface CreateAppointmentDto {
  title: string
  leadId?: string | null
  description?: string
  color?: AppointmentColor
  isAllDay?: boolean
  allDayDate?: string
  startAt?: string
  endAt?: string | null
  proformaId?: string | null
}

export type UpdateAppointmentDto = Partial<CreateAppointmentDto>

export interface LeadOption {
  id: string
  label: string
  phone: string | null
  email: string | null
}

export const appointmentsApi = {
  list: (companyId: string, params: { from: string; to: string; color?: AppointmentColor }) =>
    api.get<Appointment[]>(`/companies/${companyId}/appointments`, { params }),

  search: (companyId: string, q: string) =>
    api.get<Appointment[]>(`/companies/${companyId}/appointments/search`, { params: { q } }),

  searchLeads: (companyId: string, q: string) =>
    api.get<LeadOption[]>(`/companies/${companyId}/appointments/leads`, { params: { q } }),

  // No q + a leadId preloads that lead's proformas; q searches number or lead name/phone/email.
  searchProformas: (companyId: string, params: { q?: string; leadId?: string }) =>
    api.get<ProformaOption[]>(`/companies/${companyId}/appointments/proformas`, { params }),

  getOne: (companyId: string, id: string) =>
    api.get<Appointment>(`/companies/${companyId}/appointments/${id}`),

  create: (companyId: string, dto: CreateAppointmentDto) =>
    api.post<Appointment>(`/companies/${companyId}/appointments`, dto),

  update: (companyId: string, id: string, dto: UpdateAppointmentDto) =>
    api.patch<Appointment>(`/companies/${companyId}/appointments/${id}`, dto),

  remove: (companyId: string, id: string) =>
    api.delete(`/companies/${companyId}/appointments/${id}`),
}
